import { baseClient } from "./baseRequest";
import { uploadFileToSignedUrl } from "../utils/uploads";
import type { PendulumLogoType } from "./klydo";

export type UploadFileInput = {
  file: string;
  fileType?: string;
  fileName?: string;
  normalize?: boolean;
  // Only read by uploadPendulumLogo: "full" bakes the image to fill the whole bob.
  pendulumLogoType?: PendulumLogoType;
  onUploadProgress?: (progress: number) => void;
  options?: {
    crop?: string;
    width?: number;
    height?: number;
  };
};

export type UploadResponseStatus = {
  status: "rejected" | "approved" | "error";
  error?: string;
};

export type UploadResponse = UploadResponseStatus & {
  url: string;
  duration?: number;
  checksum?: string;
};

export type SignedUrlResponse = {
  signedUrl: string;
  fileKey: string;
};

export type UploadsApiClient = {
  uploadProfileImage: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadKlydoLoop: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadPendulumLogo: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadChime: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadTemporarySound: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadStrike: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadAlarm: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadCollectionCover: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadCollectionLogo: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadCollectionLogotype: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadCollectionSplashBackground: (
    input: UploadFileInput,
  ) => Promise<UploadResponse>;
  uploadCollectionMenuLogo: (input: UploadFileInput) => Promise<UploadResponse>;
  uploadCollectionThumbnailBackground: (
    input: UploadFileInput,
  ) => Promise<UploadResponse>;
  uploadOnboardingImages: (files: string[]) => Promise<{ urls: string[] }>;
  uploadExploreCategoryLogo: (
    input: UploadFileInput,
  ) => Promise<UploadResponse>;
};

const uploadsApiClient: UploadsApiClient = {
  uploadProfileImage: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-profile-image",
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },

  uploadKlydoLoop: async (input) => {
    const SignedUrlResponse = await baseClient.post<SignedUrlResponse>({
      url: "/uploads/generate-klydo-loop-signed-url",
      body: {
        fileName: input.fileName,
      },
    });

    await uploadFileToSignedUrl(SignedUrlResponse.signedUrl, input.file);
    return baseClient.post<UploadResponse>({
      url: "/uploads/process-klydo-loop",
      body: {
        file: SignedUrlResponse.fileKey,
        fileType: input.fileType,
        normalize: input.normalize,
        options: input.options,
      },
    });
  },
  uploadPendulumLogo: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-pendulum-logo",

      body: {
        file: input.file,
        options: input.options,
        pendulumLogoType: input.pendulumLogoType,
      },
    });
  },
  uploadChime: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-chime",
      body: {
        file: input.file,
        fileType: input.fileType,
        fileName: input.fileName,
        normalize: input.normalize,
      },
    });
  },
  uploadTemporarySound: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-temporary-sound",
      body: {
        file: input.file,
        fileType: input.fileType,
        fileName: input.fileName,
        normalize: input.normalize,
      },
    });
  },
  uploadStrike: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-strike",
      body: {
        file: input.file,
        fileType: input.fileType,
        fileName: input.fileName,
        normalize: input.normalize,
      },
    });
  },
  uploadAlarm: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-alarm",
      body: {
        file: input.file,
        fileType: input.fileType,
        fileName: input.fileName,
        normalize: input.normalize,
      },
    });
  },
  uploadCollectionCover: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-collection-cover",
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadCollectionLogo: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-collection-logo",
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadCollectionLogotype: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-collection-logotype",
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadCollectionSplashBackground: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-collection-splash-background",
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadCollectionMenuLogo: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-collection-menu-logo",
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadCollectionThumbnailBackground: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-collection-thumbnail-background",
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
  uploadOnboardingImages: async (files: string[]) => {
    return baseClient.post<{ urls: string[] }>({
      url: "/uploads/upload-onboarding-images",
      body: { files },
    });
  },
  uploadExploreCategoryLogo: async (input) => {
    return baseClient.post<UploadResponse>({
      url: "/uploads/upload-explore-category-logo",
      body: {
        file: input.file,
        options: input.options,
      },
    });
  },
};

export default uploadsApiClient;
